import React, { RefObject, useEffect, useRef, useState } from "react";
import {
    POST_LIST_TYPES,
    PostCreationResponse,
    PostType,
} from "../../../Components/Post/Post.types";
import { PostFormModal } from "../../../Components/Post/PostForm/PostFormModal";
import { groupPostsFetcher } from "../../../DataFetchers/GroupPostsFetcher";
import {
    PostsListType,
    PostWrapperOptsType,
} from "../../../Components/DashboardComponents/PostList/PostsList.types";
import PostsList from "../../../Components/DashboardComponents/PostList/PostsList";
import { updatePostFromServer } from "../../../DataFetchers/PostDataFetcher";
import { useUserStore } from "@infrastructure/Persistence/UserState";
import {
    POST_SOURCE_TYPE,
    POST_FORM_CONTENT_INPUT,
} from "../../../Components/Post/PostForm/PostFormModal.types";
import { usePostFormModalStore } from "../Stores/PostFormModalStore";
import { useInitialUserLocationStore } from "../Stores/InitialUserLocationStore";
import { HeadlineTeaserBoxWithPostFormButtons } from "../../../Components/Group/HeadlineTeaserBox/HeadlineTeaserBox";
import { EntryNewPostButton } from "../../../Components/NewPostButton/EntryNewPostButton";
import { useGlobalConfigs } from "@infrastructure/Hooks/useGlobalConfigs";
import { PostDetailsActionType } from "../../../Components/Modals/PostDetailsModal/PostDetailsModalStore";
import { PostUpdateResponse } from "@infrastructure/Services/GroupService.types";
import { SortItemType } from "../../../Components/SortingChips/SortingChips.types";
import { Box } from "@mui/material";
import { POST_TYPES } from "@infrastructure/Services/PostService.types";
import { useGroupSelector } from "../../../Components/GroupSelector/useGroupSelector";
import { FeedActionButtons } from "../Components/FeedActionButtons/FeedActionButtons";

export function PostsListFacade({
    onPostCardClick,
    isDashboardFeed,
    postContainerRef,
}: {
    isDashboardFeed: boolean;
    onPostCardClick: (postId: string, action?: PostDetailsActionType) => void;
    postContainerRef: RefObject<HTMLDivElement>;
}) {
    const { preparedFiltersForPost } = useGroupSelector();

    const { userState } = useUserStore();
    const { mainGroupId } = useGroupSelector();

    useEffect(() => {
        const newPostsListOpt = { ...postsListOpt };
        newPostsListOpt.fetchParams.groupId = mainGroupId;
        setPostsListOpt(newPostsListOpt);
    }, [userState, mainGroupId]);

    const [selectedSortCriteria, setSelectedSortCriteria] =
        useState<SortItemType>();

    const [postsListOpt, setPostsListOpt] = useState({
        fetcher: groupPostsFetcher,
        fetchParams: {
            groupId: mainGroupId,
            sortCriteria: {
                isHighlighted: 0,
                filterActiveGames: true,
                ...preparedFiltersForPost,
            },
        },
        preparedParams: {
            boxed: true,
            showPreviewComment: true,
            onCardClickHandler: onPostCardClick,
            showCommentButton: true,
            wrapperMarginBottomValue: "16px",
        },
        maxItemCount: 5000,
    });

    useEffect(() => {
        const newPostsListOpt = { ...postsListOpt };
        if (preparedFiltersForPost) {
            newPostsListOpt.fetchParams.sortCriteria = {
                ...newPostsListOpt.fetchParams.sortCriteria,
                ...selectedSortCriteria?.criterion,
                ...preparedFiltersForPost,
            };
        } else {
            newPostsListOpt.fetchParams.sortCriteria = {
                ...newPostsListOpt.fetchParams.sortCriteria,
                ...selectedSortCriteria?.criterion,
            };
            delete newPostsListOpt.fetchParams.sortCriteria.selectedGroups;
        }

        newPostsListOpt.fetchParams.sortCriteria = {
            ...newPostsListOpt.fetchParams.sortCriteria,
        };

        setPostsListOpt(newPostsListOpt);
    }, [selectedSortCriteria, preparedFiltersForPost]);

    const { selectedPost, setSelectedPost, setIsPostFormModalOpen } =
        usePostFormModalStore();

    function openPostFormHandler(sourceType?: POST_SOURCE_TYPE) {
        setIsPostFormModalOpen(
            true,
            POST_FORM_CONTENT_INPUT.DEFAULT,
            sourceType,
        );
    }

    const onPostEdit = (post: PostType) => {
        setIsPostFormModalOpen(
            true,
            post.postData.type === POST_TYPES.POLL
                ? POST_FORM_CONTENT_INPUT.POLL_INPUT
                : POST_FORM_CONTENT_INPUT.DEFAULT,
        );
        setSelectedPost(post);
    };

    const updatePostRef = useRef(
        (id: string, stringPostId: string, post: PostType) => {},
    );

    const onPostEditedSuccessfully = (
        post: PostType,
        response: PostCreationResponse | PostUpdateResponse,
    ) => {
        if (selectedPost?.id) {
            onPostCardClick?.(post.id.toString());
        } else if (response.id) {
            onPostCardClick(response.id);
        }
    };

    const updateGroupPostFromServer = (postID: number) => {
        updatePostFromServer(postID, updatePostRef.current);
    };

    const postFormModalCloseCallback = () => {
        setSelectedPost(undefined);
    };

    const [postsAreLoading, setPostsAreLoading] = useState(true);

    const postWrapperOpts: PostWrapperOptsType = {
        isInfiniteScrollEnabled: true,
        showShareOption: false,
        onPostEdit: onPostEdit,
        openPostFormHandler: openPostFormHandler,
        updatePostFromServer: updateGroupPostFromServer,
    };

    const postsListOpts: PostsListType = {
        scrollLoaderOpts: postsListOpt,
        postWrapperOpts: postWrapperOpts,
        listType: POST_LIST_TYPES.DASHBOARD_NEWEST_POSTS,
        autoLoadDataAfterClick: false,
        loadingMoreButtonText: "Mehr anzeigen",
        setPostsAreLoading: setPostsAreLoading,
    };

    useEffect(() => {
        return () => {
            setIsPostFormModalOpen(false);
        };
    }, []);

    const { initialUserLocation, setInitialUserLocation } =
        useInitialUserLocationStore();

    const [showCreatePostFabButton, setShowCreatePostFabButton] =
        useState(false);

    const { getConfigs } = useGlobalConfigs();
    const tippspielFeedCreatePostEnabled =
        getConfigs()?.tippspielFeedCreatePostEnabled;
    const startOfPostsListRef = useRef();
    return (
        <>
            {(tippspielFeedCreatePostEnabled || isDashboardFeed) && (
                <HeadlineTeaserBoxWithPostFormButtons
                    onComponentVisible={() => {
                        setShowCreatePostFabButton(false);
                    }}
                    onComponentOutOfSight={() => {
                        const isWindowBelowStartOfPostsList: boolean =
                            startOfPostsListRef.current?.getBoundingClientRect()
                                .bottom <= window.innerHeight;
                        setShowCreatePostFabButton(
                            isWindowBelowStartOfPostsList,
                        );
                    }}
                />
            )}
            <Box ref={startOfPostsListRef} />
            <FeedActionButtons
                isLoading={postsAreLoading}
                onUpdate={setSelectedSortCriteria}
            />
            <PostsList
                ref={updatePostRef}
                {...postsListOpts}
                onPostCardClick={onPostCardClick}
                startOfPostsListRef={postContainerRef}
                showLoadMoreButton={true}
                isInsideModal={false}
            />
            {mainGroupId && (
                <PostFormModal
                    postFormModalCloseCallback={postFormModalCloseCallback}
                    data={selectedPost}
                    postFormSubmissionCallback={onPostEditedSuccessfully}
                    initialUserLocation={initialUserLocation}
                    setInitialUserLocation={setInitialUserLocation}
                />
            )}
            {(tippspielFeedCreatePostEnabled || isDashboardFeed) &&
                showCreatePostFabButton && (
                    <EntryNewPostButton
                        onClick={() => {
                            setIsPostFormModalOpen(
                                true,
                                POST_FORM_CONTENT_INPUT.DEFAULT,
                                POST_SOURCE_TYPE.CREATE_POST_FAB,
                            );
                        }}
                    />
                )}
        </>
    );
}
